import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Truck, Plus, Search, Loader2, Download, Trash2, Edit3, AlertTriangle, Wrench
} from "lucide-react";
import { FALLBACK_VEHICLES } from "@/data/dummyData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const CATEGORIES = ["Car", "Bus", "Truck", "Motorcycle", "Van", "Ambulance", "Fire Truck"];
const CAR_TYPES = ["Saloon", "Hatchback", "SUV", "MPV", "Coupe", "Convertible", "Pickup", "Minibus", "Coach", "Lorry"];
const STATUS_OPTS = ["Serviceable", "Not Serviceable"];
const ACTION_OPTS = ["Nothing", "Repair", "Decommission"];

const emptyForm = {
  category: "Car", car_type: "", make: "", registration: "", description: "",
  year_model: "", status: "Serviceable", location: "",
  assigned_name: "", assigned_appointment: "", action_needed: "Nothing",
};

export default function LogisticsFleet() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ category: "", status: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [actionDialog, setActionDialog] = useState(null); // {id, action, notes: ""}

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      if (search) params.search = search;
      const res = await axios.get(`${API}/logistics/fleet/vehicles`, { params, headers: getHeaders() });
      setVehicles(res.data.length > 0 ? res.data : FALLBACK_VEHICLES);
    } catch { setVehicles(FALLBACK_VEHICLES); toast.error("Failed to load. Showing demo data."); }
    setLoading(false);
  }, [filters, search]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const openAdd = () => { setEditId(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (v) => {
    setEditId(v.id);
    setForm({
      category: v.category || "Car", car_type: v.car_type || "", make: v.make || "",
      registration: v.registration || "", description: v.description || "",
      year_model: v.year_model?.toString() || "", status: v.status || "Serviceable",
      location: v.location || "", assigned_name: v.assigned_name || "",
      assigned_appointment: v.assigned_appointment || "", action_needed: v.action_needed || "Nothing",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.registration.trim()) { toast.error("Registration is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, year_model: form.year_model ? parseInt(form.year_model) : null };
      if (editId) {
        await axios.put(`${API}/logistics/fleet/vehicles/${editId}`, payload, { headers: getHeaders() });
        toast.success("Vehicle updated");
      } else {
        await axios.post(`${API}/logistics/fleet/vehicles`, payload, { headers: getHeaders() });
        toast.success("Vehicle created");
      }
      setDialogOpen(false);
      fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/logistics/fleet/vehicles/${deleteId}`, { headers: getHeaders() });
      toast.success("Deleted"); setDeleteId(null); fetchVehicles();
    } catch { toast.error("Failed to delete"); }
  };

  const handleAction = async () => {
    if (!actionDialog) return;
    try {
      await axios.put(`${API}/logistics/fleet/vehicles/${actionDialog.id}/action`,
        { action: actionDialog.action, notes: actionDialog.notes }, { headers: getHeaders() });
      toast.success(`Vehicle marked for ${actionDialog.action}`);
      setActionDialog(null);
      fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/logistics/fleet/export`, { headers: getHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "logistics_vehicles.csv"; a.click();
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">FLEET</h1>
            <p className="text-slate-600">Vehicle Fleet Management</p>
          </div>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center gap-3 flex-wrap">
        <Search className="w-4 h-4 text-slate-400" />
        <Input placeholder="Search vehicles..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-48 bg-app border-slate-300 rounded-none" />
        <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-36 bg-app border-slate-300 rounded-none"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-300 rounded-none">
            <SelectItem value="all">All</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-36 bg-app border-slate-300 rounded-none"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-300 rounded-none">
            <SelectItem value="all">All</SelectItem>
            {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={openAdd} className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-none text-white">
          <Plus className="w-4 h-4 mr-1" /> Add Vehicle
        </Button>
        <Button onClick={handleExport} variant="outline" className="rounded-none border-slate-300">
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
      ) : vehicles.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
          <Truck className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No vehicles found.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-app border-b border-slate-200">
              <tr>
                {["Make / Model", "Registration", "Category", "Year", "Status", "Action", "Location", "Assigned To", ""].map((h) => (
                  <th key={h} className="text-left p-3 font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.id} className="border-b border-slate-100 hover:bg-app">
                  <td className="p-3">
                    <p className="font-medium text-slate-800">{v.make || "-"}</p>
                    <p className="text-xs text-slate-400">{v.car_type || v.category}</p>
                  </td>
                  <td className="p-3 text-xs font-mono text-slate-500">{v.registration}</td>
                  <td className="p-3 text-xs text-slate-500">{v.category}</td>
                  <td className="p-3 text-xs text-slate-500">{v.year_model || "-"}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 font-medium ${v.status === "Serviceable" ? "text-green-600 bg-green-50" : "text-red-600 bg-red-50"}`}>
                      {v.status}
                    </span>
                  </td>
                  <td className="p-3">
                    {v.action_needed && v.action_needed !== "Nothing" ? (
                      <div className="flex items-center gap-1">
                        <span className={`text-xs px-2 py-0.5 font-medium ${v.action_needed === "Repair" ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50"}`}>
                          {v.action_needed}
                        </span>
                        <button onClick={() => setActionDialog({ id: v.id, action: v.action_needed.toLowerCase(), notes: "" })}
                          className="text-xs text-amber-600 hover:underline">Execute</button>
                      </div>
                    ) : <span className="text-xs text-slate-400">-</span>}
                  </td>
                  <td className="p-3 text-xs text-slate-500">{v.location || "-"}</td>
                  <td className="p-3"><p className="text-xs text-slate-700">{v.assigned_name || "-"}</p></td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(v)} className="p-1 text-slate-400 hover:text-amber-600"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteId(v.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">{editId ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v, car_type: v !== "Car" ? "" : f.car_type }))}>
              <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-300 rounded-none">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.category === "Car" && (
              <Select value={form.car_type} onValueChange={(v) => setForm((f) => ({ ...f, car_type: v }))}>
                <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Type of Car" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-300 rounded-none">
                  {CAR_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Input placeholder="Make (e.g. Toyota, BMW)" value={form.make} onChange={(e) => setForm((f) => ({ ...f, make: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Input placeholder="Registration * (e.g. ABC 123 GP)" value={form.registration} onChange={(e) => setForm((f) => ({ ...f, registration: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <textarea placeholder="Description (colour, special features)" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={2} />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Year Model" type="number" max={new Date().getFullYear()} value={form.year_model} onChange={(e) => setForm((f) => ({ ...f, year_model: e.target.value }))} className="rounded-none bg-app border-slate-300" />
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-300 rounded-none">
                  {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input placeholder="Location (Yard name)" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Input placeholder="Assigned To (Name)" value={form.assigned_name} onChange={(e) => setForm((f) => ({ ...f, assigned_name: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Input placeholder="Appointment Number (AP-XXXXX)" value={form.assigned_appointment} onChange={(e) => setForm((f) => ({ ...f, assigned_appointment: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Select value={form.action_needed} onValueChange={(v) => setForm((f) => ({ ...f, action_needed: v }))}>
              <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Action Needed" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-300 rounded-none">
                {ACTION_OPTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> {editId ? "Update Vehicle" : "Create Vehicle"}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <AlertDialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Execute {actionDialog?.action === "repair" ? "Repair" : "Decommission"}?</AlertDialogTitle>
            <AlertDialogDescription>
              <textarea
                placeholder="Add notes about this action..."
                value={actionDialog?.notes || ""}
                onChange={(e) => setActionDialog((a) => a ? { ...a, notes: e.target.value } : null)}
                className="w-full mt-2 px-3 py-2 border border-slate-300 bg-app rounded-none text-sm"
                rows={3}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAction} className="rounded-none bg-amber-600 hover:bg-amber-500">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader><AlertDialogTitle>Delete Vehicle?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
